import { FC, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { pxToRem } from "../../../util";
import { Colors } from "../../../configs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { getRefundsForStationAction } from "../../../redux/actions/cashierStationActions";
import { set } from "lodash";
import { IRefundResponse } from "../../../typings/interfaces/responses/cashierStationResponses";

interface IStationRefundsModalProps {
  open: boolean;
  onClose: () => void;
  cashierId: number | null;
  stationName: string | null;
}

const sampleData = [
  { method: "Cash", transactions: 3, amount: "$5.34" },
  { method: "Credit Cards", transactions: 2, amount: "$10.25" },
  { method: "Checks", transactions: 4, amount: "$21.00" },
  { method: "Store Credits", transactions: 1, amount: "$11.00" },
];

const commonCellStyles = {
  textAlign: "center",
  verticalAlign: "middle",
  padding: "0.5rem",
  height: pxToRem(52),
};

const getBorderRadius = (
  rowIndex: number,
  cellIndex: number,
  isLastRow: boolean
) => {
  if (rowIndex === 0) {
    return cellIndex === 0
      ? `${pxToRem(10)} 0 0 0`
      : cellIndex === 2
      ? `0 ${pxToRem(10)} 0 0`
      : "0";
  }
  if (isLastRow) {
    return cellIndex === 0
      ? `0 0 0 ${pxToRem(10)}`
      : cellIndex === 2
      ? `0 0 ${pxToRem(10)} 0`
      : "0";
  }
  return "0";
};

const StationRefundsModal: FC<IStationRefundsModalProps> = ({
  open,
  onClose,
  cashierId,
  stationName,
}) => {
  const { activeStation, refundsForStation } = useAppSelector(
    (state) => state.cashierStation
  );
  const dispatch = useAppDispatch();
  const [refunds, setRefunds] = useState<IRefundResponse[]>([]);

  useEffect(() => {
    if (cashierId !== null) {
      dispatch(getRefundsForStationAction(cashierId))
        .unwrap()
        .then((res) => {
          setRefunds(res);
        })
        .catch((err) => {
          console.error("Failed to fetch refunds:", err);
          setRefunds([]);
        });
    }
  }, [cashierId, dispatch]);

  const formattedRefunds = [
    {
      method: "Cash",
      transactions: Array.isArray(refunds)
        ? refunds.filter((r) => r.cashRefundTotal > 0).length
        : 0,
      amount: Array.isArray(refunds)
        ? refunds
            .reduce((sum, r) => sum + (r.cashRefundTotal || 0), 0)
            .toFixed(2)
        : "0.00",
    },
    {
      method: "Credit Cards",
      transactions: Array.isArray(refunds)
        ? refunds.filter((r) => r.cardRefundTotal > 0).length
        : 0,
      amount: Array.isArray(refunds)
        ? refunds
            .reduce((sum, r) => sum + (r.cardRefundTotal || 0), 0)
            .toFixed(2)
        : "0.00",
    },
    {
      method: "Checks",
      transactions: 0,
      amount: "0.00",
    },
    {
      method: "Store Credits",
      transactions: Array.isArray(refunds)
        ? refunds.filter((r) => r.storeCreditRefundTotal > 0).length
        : 0,
      amount: Array.isArray(refunds)
        ? refunds
            .reduce((sum, r) => sum + (r.storeCreditRefundTotal || 0), 0)
            .toFixed(2)
        : "0.00",
    },
  ];

  return (
    <CustomModal
      title={`Refunds - ${stationName ?? "N/A"}`}
      openModal={open}
      onClose={onClose}
      width={pxToRem(657)}
      height={pxToRem(540)}
    >
      <TableContainer
        sx={{
          borderRadius: pxToRem(10),
          overflow: "hidden",
          marginTop: "3rem",
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{ backgroundColor: Colors.SECONDARY, maxHeight: pxToRem(32) }}
            >
              {["Refund Method", "No. of Transactions", "Amount ($)"].map(
                (header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      ...commonCellStyles,
                      color: Colors.WHITE,
                      borderBottom: `${pxToRem(3)} solid ${Colors.WHITE}`,
                      borderRight: `${pxToRem(3)} solid ${Colors.WHITE}`,
                      borderRadius: getBorderRadius(0, index, false),
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}
                  >
                    {header}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {formattedRefunds.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  backgroundColor: Colors.BACKSHADE_GRAY,
                  height: pxToRem(52),
                }}
              >
                {[row.method, row.transactions, `$${row.amount}`].map(
                  (cell, cellIndex) => (
                    <TableCell
                      key={cellIndex}
                      sx={{
                        ...commonCellStyles,
                        color: Colors.MAIN_GRAY,
                        borderBottom: `${pxToRem(3)} solid ${Colors.WHITE}`,
                        borderRight: `${pxToRem(3)} solid ${Colors.WHITE}`,
                        borderRadius: getBorderRadius(
                          rowIndex + 1,
                          cellIndex,
                          false
                        ),
                      }}
                    >
                      {cell}
                    </TableCell>
                  )
                )}
              </TableRow>
            ))}

            <TableRow
              sx={{ backgroundColor: Colors.MAIN_GRAY, height: pxToRem(52) }}
            >
              {[
                "Total Refunds",
                Array.isArray(refunds) ? refunds.length : 0,
                `$${
                  Array.isArray(refunds)
                    ? refunds
                        .reduce((sum, r) => sum + (r.refundTotal || 0), 0)
                        .toFixed(2)
                    : "0.00"
                }`,
              ].map((cell, index) => (
                <TableCell
                  key={index}
                  sx={{
                    ...commonCellStyles,
                    color: Colors.WHITE,
                    borderRight: `${pxToRem(3)} solid ${Colors.WHITE}`,
                    borderRadius: getBorderRadius(
                      formattedRefunds.length + 1,
                      index,
                      true
                    ),
                  }}
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </CustomModal>
  );
};

export default StationRefundsModal;
